import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, ArrowLeft, Download, CheckCircle2, Circle, Calendar, MapPin, Users, ArrowUpDown, StickyNote, Phone, Mail, User as UserIcon, X, BedDouble, Award, FileText, ShieldCheck, Utensils, CameraOff, Bell, Send, ClipboardList } from 'lucide-react';
import RichEmailEditor, { richTextIsEmpty } from '../../components/RichEmailEditor';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const sym = (c) => (c === 'inr' ? '₹' : '$');
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No date'; } catch { return iso; } };
const TIER_LABELS = { basic: 'Seekers Community', gold: 'Yearly Gold', diamond: '5-Year Diamond', lifetime: 'Lifetime Residential' };

const AdminCheckin = () => {
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date | title
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null); // program_id
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [rooms, setRooms] = useState({}); // booking_id -> room input value
  const [contact, setContact] = useState(null); // { loading, user }
  const [noteTarget, setNoteTarget] = useState(null); // registrant being noted
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [tab, setTab] = useState('registrants'); // registrants | rooms
  const [roomsData, setRoomsData] = useState(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [waiverEditor, setWaiverEditor] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [waiverSaving, setWaiverSaving] = useState(false);
  const [broadcast, setBroadcast] = useState(false);
  const [bcSubject, setBcSubject] = useState('');
  const [bcMessage, setBcMessage] = useState('');
  const [bcAudience, setBcAudience] = useState('all');
  const [bcSending, setBcSending] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderCfg, setReminderCfg] = useState(null);
  const [savingReminder, setSavingReminder] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    api.get('/admin/crm/settings/reminders').then((r) => setReminderCfg({ ...r.data, daysStr: (r.data.days_before || []).join(', '), daysAfterStr: (r.data.days_after || []).join(', ') })).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setLogOpen(false); setReminderOpen(false); setBroadcast(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const saveReminderCfg = async () => {
    setSavingReminder(true);
    try {
      const days = (reminderCfg.daysStr || '').split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
      const daysAfter = (reminderCfg.daysAfterStr || '').split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));
      const res = await api.put('/admin/crm/settings/reminders', {
        enabled: reminderCfg.enabled, days_before: days.length ? days : [3],
        subject: reminderCfg.subject, intro: reminderCfg.intro,
        channels: reminderCfg.channels || { email: true, sms: false, whatsapp: false },
        sms_text: reminderCfg.sms_text,
        post_enabled: !!reminderCfg.post_enabled, days_after: daysAfter.length ? daysAfter : [2],
        post_subject: reminderCfg.post_subject, post_intro: reminderCfg.post_intro,
        post_sms_text: reminderCfg.post_sms_text,
        photo_invite_enabled: !!reminderCfg.photo_invite_enabled,
        photo_days_after: parseInt(reminderCfg.photo_days_after, 10) || 1,
        photo_subject: reminderCfg.photo_subject, photo_intro: reminderCfg.photo_intro,
      });
      setReminderCfg({ ...res.data, daysStr: (res.data.days_before || []).join(', '), daysAfterStr: (res.data.days_after || []).join(', ') });
      toast({ title: 'Reminder settings saved' });
      setReminderOpen(false);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingReminder(false); }
  };

  const sendReminderNow = async (phase = 'pre') => {
    const label = phase === 'post' ? 'post-event follow-up' : 'reminder';
    if (!window.confirm(`Send a ${label} to all confirmed registrants of this program now, across your enabled channels?`)) return;
    setSendingReminder(true);
    try {
      const res = await api.post(`/admin/crm/programs/${selected}/send-reminder?phase=${phase}`);
      toast({ title: `${phase === 'post' ? 'Follow-up' : 'Reminder'} sent`, description: `${res.data.sent} sent · ${res.data.skipped} skipped · ${res.data.failed} failed` });
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSendingReminder(false); }
  };

  const sendBroadcast = async () => {
    if (bcSubject.trim().length < 2 || richTextIsEmpty(bcMessage)) { toast({ title: 'Add a subject and message.' }); return; }
    setBcSending(true);
    try {
      const res = await api.post(`/admin/crm/programs/${selected}/broadcast`, { subject: bcSubject, message: bcMessage, audience: bcAudience });
      toast({ title: 'Queued for sending', description: `${res.data.queued} recipient(s) — sending in the background.` });
      setBroadcast(false); setBcSubject(''); setBcMessage(''); setBcAudience('all');
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setBcSending(false); }
  };

  const [logOpen, setLogOpen] = useState(false);
  const [logData, setLogData] = useState(null);
  const openReminderLog = async () => {
    setLogOpen(true); setLogData(null);
    try { const res = await api.get(`/admin/crm/programs/${selected}/reminder-log`); setLogData(res.data); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const fetchPrograms = async () => {
    setLoading(true);
    try { const res = await api.get('/admin/checkin/programs'); setPrograms(res.data || []); }
    catch (e) { console.error('Failed to load programs:', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchPrograms(); }, []);

  const openProgram = async (pid) => {
    setSelected(pid); setDetail(null); setDetailLoading(true); setTab('registrants'); setRoomsData(null);
    try {
      const res = await api.get(`/admin/checkin/programs/${pid}`);
      setDetail(res.data);
      const r = {};
      (res.data.registrants || []).forEach((x) => {
        r[x.booking_id] = x.room || '';
        (x.guests || []).forEach((g) => { r[`${x.booking_id}:g${g.index}`] = g.room || ''; });
      });
      setRooms(r);
    } catch (e) { console.error('Failed to load registrants:', e); }
    finally { setDetailLoading(false); }
  };

  const toggleCheckin = async (reg) => {
    setSavingId(reg.booking_id);
    try {
      const next = !reg.checked_in;
      await api.patch(`/admin/bookings/${reg.booking_id}/checkin`, { checked_in: next, room: rooms[reg.booking_id] || '' });
      toast({ title: next ? 'Checked in' : 'Check-in removed', description: reg.name });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const saveRoom = async (reg) => {
    setSavingId(reg.booking_id);
    try {
      await api.patch(`/admin/bookings/${reg.booking_id}/checkin`, { checked_in: reg.checked_in, room: rooms[reg.booking_id] || '' });
      toast({ title: 'Room saved', description: `${reg.name} → ${rooms[reg.booking_id] || '—'}` });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const toggleGuestCheckin = async (reg, g) => {
    const key = `${reg.booking_id}:g${g.index}`;
    setSavingId(key);
    try {
      const next = !g.checked_in;
      await api.patch(`/admin/bookings/${reg.booking_id}/guests/${g.index}/checkin`, { checked_in: next, room: rooms[key] || '' });
      toast({ title: next ? 'Checked in' : 'Check-in removed', description: g.name });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const saveGuestRoom = async (reg, g) => {
    const key = `${reg.booking_id}:g${g.index}`;
    setSavingId(key);
    try {
      await api.patch(`/admin/bookings/${reg.booking_id}/guests/${g.index}/checkin`, { checked_in: g.checked_in, room: rooms[key] || '' });
      toast({ title: 'Room saved', description: `${g.name} → ${rooms[key] || '—'}` });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const openContact = async (reg) => {
    setContact({ loading: true, user: null, reg });
    try {
      const res = await api.get(`/admin/users/${reg.user_id}`);
      setContact({ loading: false, user: res.data, reg });
    } catch (e) {
      setContact({ loading: false, user: null, reg, error: true });
    }
  };

  const openNote = (reg) => { setNoteTarget(reg); setNoteText(reg.notes || ''); };

  const saveNote = async () => {
    if (!noteTarget) return;
    setNoteSaving(true);
    try {
      await api.patch(`/admin/bookings/${noteTarget.booking_id}/notes`, { notes: noteText });
      toast({ title: 'Note saved' });
      setNoteTarget(null);
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setNoteSaving(false); }
  };

  const markGuestWaiver = async (reg, g) => {
    const key = `w:${reg.booking_id}:g${g.index}`;
    setSavingId(key);
    try {
      await api.patch(`/admin/bookings/${reg.booking_id}/waiver`, { guest_index: g.index, signed: !g.waiver_signed });
      toast({ title: g.waiver_signed ? 'Waiver unmarked' : 'Waiver marked signed', description: g.name });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const markBookingWaiver = async (reg) => {
    const key = `w:${reg.booking_id}`;
    setSavingId(key);
    try {
      await api.patch(`/admin/bookings/${reg.booking_id}/waiver`, { signed: !reg.waiver_signed });
      toast({ title: reg.waiver_signed ? 'Waiver unmarked' : 'Waiver marked signed', description: reg.name });
      await openProgram(selected);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingId(null); }
  };

  const loadRooms = async () => {
    setRoomsLoading(true);
    try { const res = await api.get(`/admin/checkin/programs/${selected}/rooms`); setRoomsData(res.data); }
    catch (e) { console.error('Failed to load rooms:', e); }
    finally { setRoomsLoading(false); }
  };

  useEffect(() => { if (selected && tab === 'rooms') loadRooms(); /* eslint-disable-next-line */ }, [tab, selected]);

  const openWaiverEditor = async () => {
    try { const res = await api.get('/admin/settings/waiver'); setWaiverText(res.data.text || ''); }
    catch (e) { console.error('Failed to load waiver text:', e); }
    setWaiverEditor(true);
  };

  const saveWaiverText = async () => {
    setWaiverSaving(true);
    try { await api.put('/admin/settings/waiver', { text: waiverText }); toast({ title: 'Waiver text saved' }); setWaiverEditor(false); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setWaiverSaving(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/checkin/programs/${selected}/export`, { responseType: 'blob' });
      const title = (detail?.program?.title || 'event').replace(/[^A-Za-z0-9 _-]/g, '').trim().replace(/\s+/g, '_') || 'event';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${title}_checkin.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Export failed', description: e?.response?.data?.detail }); }
    finally { setExporting(false); }
  };

  const visiblePrograms = useMemo(() => {
    let list = programs.filter((p) => !q || (p.program_title || '').toLowerCase().includes(q.toLowerCase()));
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortBy === 'title') { av = (a.program_title || '').toLowerCase(); bv = (b.program_title || '').toLowerCase(); }
      else { av = a.start_datetime || ''; bv = b.start_datetime || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [programs, q, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'title' ? 'asc' : 'desc'); }
  };

  // ---------- Detail (registrants) view ----------
  if (selected) {
    const t = detail?.totals || {};
    const waiverReq = !!detail?.program?.waiver_required;
    const photoOptOuts = [];
    (detail?.registrants || []).forEach((r) => {
      if (r.guests?.length) {
        r.guests.forEach((g) => { if (g.media_optout) photoOptOuts.push({ name: g.name || `Guest ${g.index + 1}`, booker: r.name }); });
      } else if (r.media_optout) {
        photoOptOuts.push({ name: r.name, booker: r.name });
      }
    });
    return (
      <div className="space-y-6" data-testid="checkin-detail-page">
        <button onClick={() => { setSelected(null); setDetail(null); fetchPrograms(); }}
          data-testid="checkin-back" className="flex items-center gap-2 text-sm text-mahogany/80 hover:text-mahogany">
          <ArrowLeft className="w-4 h-4" /> All programs
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow mb-2">Check-in</div>
            <h1 className="font-serif-display text-4xl text-burgundy">{detail?.program?.title || '…'}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-mahogany/70 mt-2">
              {detail?.program?.start_datetime && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(detail.program.start_datetime)}</span>}
              {detail?.program?.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {detail.program.location}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportXlsx} disabled={exporting} data-testid="checkin-export"
              className="pill-btn inline-flex items-center gap-2 text-sm">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export Excel
            </button>
            <button onClick={() => setBroadcast(true)} data-testid="checkin-broadcast"
              className="pill-btn-outline inline-flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" /> Message registrants
            </button>
            <button onClick={() => sendReminderNow('pre')} disabled={sendingReminder} data-testid="checkin-send-reminder"
              className="pill-btn-outline inline-flex items-center gap-2 text-sm">
              {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Send reminder now
            </button>
            <button onClick={() => sendReminderNow('post')} disabled={sendingReminder} data-testid="checkin-send-followup"
              className="pill-btn-outline inline-flex items-center gap-2 text-sm">
              {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Send follow-up now
            </button>
            <button onClick={() => sendReminderNow('photo')} disabled={sendingReminder} data-testid="checkin-send-photo-invite"
              className="pill-btn-outline inline-flex items-center gap-2 text-sm">
              {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Send photo invite now
            </button>
            <button onClick={openReminderLog} data-testid="checkin-reminder-log"
              className="pill-btn-outline inline-flex items-center gap-2 text-sm">
              <ClipboardList className="w-4 h-4" /> Reminder log
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Bookings" value={t.bookings || 0} />
          <Stat label="Registrants" value={t.registrants || 0} />
          <Stat label="Checked In" value={`${t.checked_in || 0} / ${t.registrants || 0}`} />
          <Stat label="Revenue" value={`$${(t.revenue || 0).toLocaleString()}`} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-rose/40">
          <button onClick={() => setTab('registrants')} data-testid="tab-registrants"
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === 'registrants' ? 'border-burgundy text-burgundy font-medium' : 'border-transparent text-mahogany/60 hover:text-mahogany'}`}>
            Registrants
          </button>
          <button onClick={() => setTab('rooms')} data-testid="tab-rooms"
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === 'rooms' ? 'border-burgundy text-burgundy font-medium' : 'border-transparent text-mahogany/60 hover:text-mahogany'}`}>
            <BedDouble className="w-3.5 h-3.5" /> Room Occupancy
          </button>
          <button onClick={() => setTab('photos')} data-testid="tab-photos"
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === 'photos' ? 'border-burgundy text-burgundy font-medium' : 'border-transparent text-mahogany/60 hover:text-mahogany'}`}>
            <CameraOff className="w-3.5 h-3.5" /> Photo Opt-outs{photoOptOuts.length > 0 ? ` (${photoOptOuts.length})` : ''}
          </button>
        </div>

        {tab === 'photos' ? (
          <div className="space-y-4" data-testid="photos-view">
            <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
              <div className="font-medium mb-1">Photography consent policy</div>
              The guests below have <b>opted out</b> of photo/video use. Please do not include them in photos or social-media content.
              <span className="block mt-1 text-amber-800/90">Note: if an opted-out guest voluntarily takes part in a photo or video, they are considered to have given their consent for its use.</span>
            </div>
            {photoOptOuts.length === 0 ? (
              <div className="text-mahogany/60 text-sm">No photo opt-outs — everyone has consented to photography.</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="photo-optout-list">
                {photoOptOuts.map((o, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-white border border-rose/50 text-burgundy" data-testid={`photo-optout-${idx}`}>
                    <CameraOff className="w-3.5 h-3.5 text-red-500" /> {o.name}
                    {o.booker !== o.name && <span className="text-mahogany/50 text-xs">· {o.booker}'s group</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'rooms' ? (
          roomsLoading ? (
            <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="space-y-5" data-testid="rooms-view">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(roomsData?.rooms || []).map((rm) => (
                  <div key={rm.room} className="p-4 rounded-2xl border border-rose/50 bg-white/70" data-testid={`room-card-${rm.room}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-burgundy font-medium"><BedDouble className="w-4 h-4" /> Room {rm.room}</div>
                      <span className="text-xs text-mahogany/60">{rm.checked_in}/{rm.count} in</span>
                    </div>
                    <ul className="space-y-1.5">
                      {rm.occupants.map((o, idx) => (
                        <li key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-burgundy">{o.name}{o.registrants > 1 ? ` (+${o.registrants - 1})` : ''}</span>
                          {o.checked_in
                            ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                            : <Circle className="w-4 h-4 text-mahogany/30" />}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {(roomsData?.rooms || []).length === 0 && (
                  <div className="text-mahogany/60 text-sm">No rooms assigned yet. Assign rooms in the Registrants tab.</div>
                )}
              </div>
              {roomsData?.unassigned_count > 0 && (
                <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50" data-testid="rooms-unassigned">
                  <div className="text-sm font-medium text-amber-800 mb-2">Not yet assigned a room ({roomsData.unassigned_count})</div>
                  <div className="flex flex-wrap gap-2">
                    {roomsData.unassigned.map((o, idx) => (
                      <span key={idx} className="text-xs px-2.5 py-1 rounded-full bg-white text-mahogany border border-amber-200">{o.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        ) : detailLoading ? (
          <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white/70 rounded-2xl overflow-hidden border border-rose/50">
              <thead className="bg-rose-soft/60">
                <tr className="text-left text-xs uppercase tracking-widest text-mahogany">
                  <th className="px-4 py-3">Guest</th>
                  <th className="px-4 py-3">Registrants</th>
                  <th className="px-4 py-3">Payment / Waiver</th>
                  <th className="px-4 py-3">Room</th>
                  <th className="px-4 py-3">Check-in</th>
                </tr>
              </thead>
              <tbody data-testid="checkin-table-body">
                {(detail?.registrants || []).map((r) => (
                  r.guests && r.guests.length > 0 ? (
                    <React.Fragment key={r.booking_id}>
                      <tr className="border-t border-rose/40 bg-rose-soft/10">
                        <td className="px-4 py-3">
                          <div className="text-burgundy font-medium">{r.name} <span className="text-xs text-mahogany/60">· group of {r.guests.length}</span></div>
                          <div className="text-xs text-mahogany/70">{r.email}</div>
                          <RowActions reg={r} onContact={openContact} onNote={openNote} />
                        </td>
                        <td className="px-4 py-3 text-sm text-burgundy">{r.registrants}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-cream-2 text-mahogany capitalize">{r.payment_status}</span>
                          <span className="ml-2 text-sm text-burgundy">{sym(r.currency)}{Number(r.amount).toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-mahogany/60" colSpan={2}>Assign a room &amp; check in each guest below</td>
                      </tr>
                      {r.guests.map((g) => {
                        const key = `${r.booking_id}:g${g.index}`;
                        const wkey = `w:${r.booking_id}:g${g.index}`;
                        const blocked = waiverReq && !g.waiver_signed;
                        return (
                          <tr key={key} className="border-t border-rose/20 hover:bg-rose-soft/20">
                            <td className="px-4 py-2.5 pl-10">
                              <div className="text-burgundy text-sm">{g.name || `Guest ${g.index + 1}`}</div>
                              {g.dietary && <DietaryChip text={g.dietary} />}
                            </td>
                            <td className="px-4 py-2.5" />
                            <td className="px-4 py-2.5">
                              <WaiverCell signed={g.waiver_signed} by={g.waiver_signed_by} media_optout={g.media_optout}
                                saving={savingId === wkey} onMark={() => markGuestWaiver(r, g)} testid={wkey} />
                            </td>
                            <td className="px-4 py-2.5">
                              <input value={rooms[key] || ''} onChange={(e) => setRooms({ ...rooms, [key]: e.target.value })}
                                onBlur={() => { if ((rooms[key] || '') !== (g.room || '')) saveGuestRoom(r, g); }}
                                placeholder="e.g. 204" data-testid={`room-input-${key}`}
                                className="w-28 bg-white rounded-lg border border-rose/50 px-2 py-1 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                            </td>
                            <td className="px-4 py-2.5">
                              <button onClick={() => toggleGuestCheckin(r, g)} disabled={savingId === key || blocked}
                                data-testid={`checkin-toggle-${key}`} title={blocked ? 'Waiver must be signed first' : ''}
                                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${blocked ? 'bg-cream-2 text-mahogany/40 cursor-not-allowed' : g.checked_in ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-cream-2 text-mahogany hover:bg-rose-soft'}`}>
                                {savingId === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : g.checked_in ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                                {g.checked_in ? 'Checked In' : 'Check In'}
                              </button>
                              {blocked && <div className="text-[10px] text-amber-700 mt-1">Waiver needed</div>}
                              {g.checked_in && g.checked_in_at && <div className="text-[10px] text-mahogany/60 mt-1">{fmtDate(g.checked_in_at)}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ) : (
                    <tr key={r.booking_id} className="border-t border-rose/40 hover:bg-rose-soft/20">
                      <td className="px-4 py-3">
                        <div className="text-burgundy font-medium">{r.name}</div>
                        <div className="text-xs text-mahogany/70">{r.email}</div>
                        {r.dietary && <DietaryChip text={r.dietary} />}
                        <RowActions reg={r} onContact={openContact} onNote={openNote} />
                      </td>
                      <td className="px-4 py-3 text-sm text-burgundy">{r.registrants}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-cream-2 text-mahogany capitalize">{r.payment_status}</span>
                        <span className="ml-2 text-sm text-burgundy">{sym(r.currency)}{Number(r.amount).toFixed(2)}</span>
                        <div className="mt-1.5">
                          <WaiverCell signed={r.waiver_signed} by={r.waiver_signed_by} media_optout={r.media_optout}
                            saving={savingId === `w:${r.booking_id}`} onMark={() => markBookingWaiver(r)} testid={`w:${r.booking_id}`} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input value={rooms[r.booking_id] || ''} onChange={(e) => setRooms({ ...rooms, [r.booking_id]: e.target.value })}
                          onBlur={() => { if ((rooms[r.booking_id] || '') !== (r.room || '')) saveRoom(r); }}
                          placeholder="e.g. 204" data-testid={`room-input-${r.booking_id}`}
                          className="w-28 bg-white rounded-lg border border-rose/50 px-2 py-1 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                      </td>
                      <td className="px-4 py-3">
                        {(() => { const blocked = waiverReq && !r.waiver_signed; return (
                          <>
                            <button onClick={() => toggleCheckin(r)} disabled={savingId === r.booking_id || blocked}
                              data-testid={`checkin-toggle-${r.booking_id}`} title={blocked ? 'Waiver must be signed first' : ''}
                              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${blocked ? 'bg-cream-2 text-mahogany/40 cursor-not-allowed' : r.checked_in ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-cream-2 text-mahogany hover:bg-rose-soft'}`}>
                              {savingId === r.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : r.checked_in ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                              {r.checked_in ? 'Checked In' : 'Check In'}
                            </button>
                            {blocked && <div className="text-[10px] text-amber-700 mt-1">Waiver needed</div>}
                            {r.checked_in && r.checked_in_at && <div className="text-[10px] text-mahogany/60 mt-1">{fmtDate(r.checked_in_at)}</div>}
                          </>
                        ); })()}
                      </td>
                    </tr>
                  )
                ))}
                {(detail?.registrants || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-mahogany/60">No confirmed registrants yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Contact popup */}
        {contact && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setContact(null)} data-testid="contact-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif-display text-2xl text-burgundy">Contact details</h3>
                <button onClick={() => setContact(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
              </div>
              {contact.loading ? (
                <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : contact.user ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rose-soft flex items-center justify-center text-mahogany font-serif-display text-lg">
                      {(contact.user.full_name || '?').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div>
                      <div className="text-burgundy font-medium" data-testid="contact-name">{contact.user.full_name}</div>
                      <div className="text-xs text-mahogany/70 uppercase tracking-widest">{TIER_LABELS[contact.user.membership?.tier || 'basic'] || 'Member'}</div>
                    </div>
                  </div>
                  <ContactRow icon={Mail} label="Email" value={contact.user.email} testid="contact-email" />
                  <ContactRow icon={Phone} label="Phone" value={contact.user.phone || 'Not provided'} testid="contact-phone" />
                  <ContactRow icon={UserIcon} label="Registrants on booking" value={contact.reg?.registrants} />
                  <ContactRow icon={Award} label="Member since" value={contact.user.created_at ? new Date(contact.user.created_at).toLocaleDateString() : '—'} />
                </div>
              ) : (
                <div className="text-mahogany/60 text-sm">Could not load contact details for this user.</div>
              )}
            </div>
          </div>
        )}

        {/* Note editor */}
        {noteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="note-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
              <h3 className="font-serif-display text-2xl text-burgundy">Note for {noteTarget.name}</h3>
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4}
                placeholder="e.g. Arriving late, dietary needs, special requests…" data-testid="note-textarea"
                className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setNoteTarget(null)} data-testid="note-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                <button onClick={saveNote} disabled={noteSaving} data-testid="note-save"
                  className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                  {noteSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save note
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Broadcast modal */}
        {broadcast && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="broadcast-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4">
              <h3 className="font-serif-display text-2xl text-burgundy">Message registrants</h3>
              <p className="text-sm text-mahogany/70">Send an email to everyone registered for {detail?.program?.title}. Sent via SendGrid and logged on each person's profile.</p>
              <div>
                <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Audience</label>
                <select value={bcAudience} onChange={(e) => setBcAudience(e.target.value)} data-testid="broadcast-audience"
                  className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">
                  <option value="all">All confirmed registrants</option>
                  <option value="checked_in">Only checked-in</option>
                  <option value="not_checked_in">Only not-yet checked-in</option>
                </select>
              </div>
              <input value={bcSubject} onChange={(e) => setBcSubject(e.target.value)} placeholder="Subject (you can use {{name}} too)"
                data-testid="broadcast-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              <div data-testid="broadcast-message">
                <RichEmailEditor value={bcMessage} onChange={setBcMessage} placeholder="Write your message… (e.g. schedule, what to bring, arrival details)" testid="broadcast-rich-editor" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setBroadcast(false)} data-testid="broadcast-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                <button onClick={sendBroadcast} disabled={bcSending} data-testid="broadcast-send"
                  className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                  {bcSending && <Loader2 className="w-4 h-4 animate-spin" />} Send email
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reminder delivery log modal */}
        {logOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reminder-log-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="font-serif-display text-2xl text-burgundy">Reminder delivery log</h3>
                <button onClick={() => setLogOpen(false)} data-testid="reminder-log-close" className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
              </div>
              {!logData ? (
                <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : logData.entries.length === 0 ? (
                <p className="text-sm text-mahogany/60">No reminders have been sent for this program yet.</p>
              ) : (
                <>
                  <div className="text-sm text-mahogany/80">{logData.sent} delivered · {logData.failed} failed · {logData.total} total</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-rose-soft/60 text-xs uppercase tracking-widest text-mahogany">
                        <tr><th className="px-3 py-2 text-left">Recipient</th><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Status</th></tr>
                      </thead>
                      <tbody>
                        {logData.entries.map((e, i) => (
                          <tr key={i} className="border-t border-rose/40">
                            <td className="px-3 py-2 text-burgundy">{e.email}</td>
                            <td className="px-3 py-2 text-mahogany/70">{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                            <td className="px-3 py-2 text-mahogany/70">{e.kind}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full ${e.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{e.status === 'sent' ? 'Delivered' : 'Failed'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="checkin-programs-page">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Program Check-in</h1>
        <p className="text-mahogany/70 text-sm mt-1">Select a program to view registrants, mark arrivals, assign rooms and export the check-in sheet.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center bg-white/70 rounded-full border border-rose/50 px-4 py-2 gap-2">
          <Search className="w-4 h-4 text-mahogany/70" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search program title…"
            data-testid="checkin-search" className="bg-transparent focus:outline-none text-burgundy w-64" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setReminderOpen(true)} data-testid="reminders-settings-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-2">
            <Bell className="w-4 h-4" /> Automated reminders{reminderCfg?.enabled ? ' · On' : ''}
          </button>
          <button onClick={openWaiverEditor} data-testid="edit-waiver-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> Edit waiver text
          </button>
        </div>
      </div>

      {/* Reminder settings modal */}
      {reminderOpen && reminderCfg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reminders-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif-display text-2xl text-burgundy">Automated reminders</h3>
            <p className="text-sm text-mahogany/70">Automatically message registrants before their program (schedule + what to bring) and follow up afterwards. Runs in the background and never sends the same person twice.</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!reminderCfg.enabled} onChange={(e) => setReminderCfg({ ...reminderCfg, enabled: e.target.checked })}
                data-testid="reminders-enabled" className="w-4 h-4 accent-burgundy" />
              <span className="text-sm text-burgundy">Enable automated reminders</span>
            </label>

            {/* Channels */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Send on</label>
              <div className="flex flex-wrap gap-4">
                {[['email', 'Email'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp']].map(([ch, label]) => (
                  <label key={ch} className="flex items-center gap-2 text-sm text-burgundy cursor-pointer">
                    <input type="checkbox" data-testid={`reminders-ch-${ch}`}
                      checked={!!(reminderCfg.channels || {})[ch]}
                      onChange={(e) => setReminderCfg({ ...reminderCfg, channels: { ...(reminderCfg.channels || {}), [ch]: e.target.checked } })}
                      className="w-4 h-4 accent-burgundy" />
                    {label}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-mahogany/60 mt-1">SMS/WhatsApp only send to contacts with a number on file, and only when that channel is configured in Zernio.</p>
            </div>

            {/* Pre-event */}
            <div className="border-t border-rose/40 pt-3 space-y-3">
              <div className="text-xs uppercase tracking-widest text-burgundy font-medium">Before the event</div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Days before (comma-separated)</label>
                <input value={reminderCfg.daysStr} onChange={(e) => setReminderCfg({ ...reminderCfg, daysStr: e.target.value })}
                  placeholder="3, 1" data-testid="reminders-days" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              </div>
              <input value={reminderCfg.subject || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, subject: e.target.value })}
                placeholder="Email subject" data-testid="reminders-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              <textarea value={reminderCfg.intro || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, intro: e.target.value })}
                rows={2} placeholder="Opening line of the reminder email" data-testid="reminders-intro" className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
              <textarea value={reminderCfg.sms_text || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, sms_text: e.target.value })}
                rows={2} placeholder="SMS / WhatsApp text (use {{name}})" data-testid="reminders-sms-text" className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
            </div>

            {/* Post-event */}
            <div className="border-t border-rose/40 pt-3 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!reminderCfg.post_enabled} onChange={(e) => setReminderCfg({ ...reminderCfg, post_enabled: e.target.checked })}
                  data-testid="reminders-post-enabled" className="w-4 h-4 accent-burgundy" />
                <span className="text-xs uppercase tracking-widest text-burgundy font-medium">After the event (follow-up)</span>
              </label>
              {reminderCfg.post_enabled && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Days after (comma-separated)</label>
                    <input value={reminderCfg.daysAfterStr} onChange={(e) => setReminderCfg({ ...reminderCfg, daysAfterStr: e.target.value })}
                      placeholder="2, 7" data-testid="reminders-days-after" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                  </div>
                  <input value={reminderCfg.post_subject || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, post_subject: e.target.value })}
                    placeholder="Follow-up email subject" data-testid="reminders-post-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                  <textarea value={reminderCfg.post_intro || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, post_intro: e.target.value })}
                    rows={2} placeholder="Opening line of the follow-up email" data-testid="reminders-post-intro" className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                  <textarea value={reminderCfg.post_sms_text || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, post_sms_text: e.target.value })}
                    rows={2} placeholder="Follow-up SMS / WhatsApp text (use {{name}})" data-testid="reminders-post-sms-text" className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                </>
              )}
            </div>

            {/* Photo invite */}
            <div className="border-t border-rose/40 pt-3 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!reminderCfg.photo_invite_enabled} onChange={(e) => setReminderCfg({ ...reminderCfg, photo_invite_enabled: e.target.checked })}
                  data-testid="reminders-photo-enabled" className="w-4 h-4 accent-burgundy" />
                <span className="text-xs uppercase tracking-widest text-burgundy font-medium">📸 Invite attendees to share photos</span>
              </label>
              {reminderCfg.photo_invite_enabled && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Days after the event</label>
                    <input type="number" min="0" max="90" value={reminderCfg.photo_days_after ?? 1} onChange={(e) => setReminderCfg({ ...reminderCfg, photo_days_after: e.target.value })}
                      data-testid="reminders-photo-days" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                  </div>
                  <input value={reminderCfg.photo_subject || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, photo_subject: e.target.value })}
                    placeholder="Photo invite email subject" data-testid="reminders-photo-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                  <textarea value={reminderCfg.photo_intro || ''} onChange={(e) => setReminderCfg({ ...reminderCfg, photo_intro: e.target.value })}
                    rows={2} placeholder="Opening line inviting them to share photos" data-testid="reminders-photo-intro" className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                  <p className="text-[11px] text-mahogany/60">A “Share your photos” button linking to the event page is added automatically.</p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setReminderOpen(false)} data-testid="reminders-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={saveReminderCfg} disabled={savingReminder} data-testid="reminders-save"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {savingReminder && <Loader2 className="w-4 h-4 animate-spin" />} Save settings
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white/70 rounded-2xl overflow-hidden border border-rose/50">
            <thead className="bg-rose-soft/60">
              <tr className="text-left text-xs uppercase tracking-widest text-mahogany">
                <th className="px-4 py-3">
                  <button onClick={() => toggleSort('title')} className="inline-flex items-center gap-1 hover:text-burgundy" data-testid="sort-title">
                    Program <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3">
                  <button onClick={() => toggleSort('date')} className="inline-flex items-center gap-1 hover:text-burgundy" data-testid="sort-date">
                    Date <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3">Registrants</th>
                <th className="px-4 py-3">Checked In</th>
                <th className="px-4 py-3">Revenue</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="checkin-programs-body">
              {visiblePrograms.map((p) => (
                <tr key={p.program_id} className="border-t border-rose/40 hover:bg-rose-soft/20 cursor-pointer" onClick={() => openProgram(p.program_id)}>
                  <td className="px-4 py-3">
                    <div className="text-burgundy font-medium">{p.program_title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-mahogany/60 capitalize">{p.program_type}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-mahogany/80 whitespace-nowrap">{fmtDate(p.start_datetime)}</td>
                  <td className="px-4 py-3 text-sm text-burgundy"><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {p.total_registrants}</span></td>
                  <td className="px-4 py-3 text-sm text-burgundy">{p.checked_in} / {p.total_registrants}</td>
                  <td className="px-4 py-3 text-sm text-burgundy">${(p.revenue || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); openProgram(p.program_id); }} data-testid={`open-program-${p.program_id}`}
                      className="pill-btn-outline text-xs py-1.5">Open</button>
                  </td>
                </tr>
              ))}
              {visiblePrograms.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-mahogany/60">No programs with confirmed registrations yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Global waiver editor */}
      {waiverEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="waiver-editor-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl text-burgundy">Global waiver text</h3>
              <button onClick={() => setWaiverEditor(false)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-mahogany/70">This is the default liability & media-release waiver guests sign. Individual programs can override it in the program editor.</p>
            <textarea value={waiverText} onChange={(e) => setWaiverText(e.target.value)} rows={14}
              data-testid="waiver-editor-textarea"
              className="w-full bg-white rounded-2xl border border-rose/50 px-3 py-2 text-xs text-burgundy focus:outline-none focus:border-mahogany font-mono leading-relaxed" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setWaiverEditor(false)} className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={saveWaiverText} disabled={waiverSaving} data-testid="waiver-editor-save"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {waiverSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save waiver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="p-4 rounded-2xl border border-rose/50 bg-white/70">
    <div className="text-xs uppercase tracking-widest text-mahogany/70">{label}</div>
    <div className="font-serif-display text-2xl text-burgundy mt-1">{value}</div>
  </div>
);

const RowActions = ({ reg, onContact, onNote }) => (
  <div className="flex items-center gap-2 mt-1.5">
    <button onClick={() => onContact(reg)} data-testid={`contact-btn-${reg.booking_id}`}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">
      <Phone className="w-3 h-3" /> Contact
    </button>
    <button onClick={() => onNote(reg)} data-testid={`note-btn-${reg.booking_id}`}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full transition-colors ${reg.notes ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-cream-2 text-mahogany hover:bg-rose-soft'}`}>
      <StickyNote className="w-3 h-3" /> {reg.notes ? 'Note' : 'Add note'}
    </button>
    {reg.notes && <span className="text-[11px] text-mahogany/60 italic truncate max-w-[200px]" title={reg.notes}>“{reg.notes}”</span>}
  </div>
);

const ContactRow = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white/60 border border-rose/40">
    <Icon className="w-4 h-4 text-mahogany/70 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-mahogany/60">{label}</div>
      <div className="text-sm text-burgundy truncate" data-testid={testid}>{value}</div>
    </div>
  </div>
);

const DietaryChip = ({ text }) => (
  <div className="mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-800" title={text}>
    <Utensils className="w-3 h-3" /> <span className="truncate max-w-[180px]">{text}</span>
  </div>
);

const WaiverCell = ({ signed, by, media_optout, saving, onMark, testid }) => (
  <div className="flex flex-col items-start gap-1">
    {signed ? (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-800" data-testid={`waiver-status-${testid}`}>
        <ShieldCheck className="w-3 h-3" /> Waiver signed{by === 'admin' ? ' (staff)' : ''}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`waiver-status-${testid}`}>
        <ShieldCheck className="w-3 h-3" /> Not signed
      </span>
    )}
    {media_optout && <span className="text-[10px] text-mahogany/60">Photo opt-out</span>}
    <button onClick={onMark} disabled={saving} data-testid={`waiver-mark-${testid}`}
      className="text-[10px] text-burgundy underline hover:text-mahogany disabled:opacity-50">
      {saving ? '…' : signed ? 'Unmark' : 'Mark signed'}
    </button>
  </div>
);

export default AdminCheckin;
